// @ts-strict-ignore
import { TextureUtils } from '../model3d/util/texture-utils';
import { CanvasTexture, Color, LinearFilter, MeshBasicMaterial, MeshBasicMaterialParameters, Texture } from 'three';
import { NumberUtils } from '@shared/util/number-utils';

export class SpriteMaterial extends MeshBasicMaterial {

  public static readonly DEFAULT_BITMAP_ROTATION: number = -Math.PI / 2;
  public static readonly MIN_TEXTURE_ANISOTROPY_VALUE: number = 2;

  private width: number = 2;
  private height: number = 2;
  private _fillColor: number;
  private _defaultRotation: number;
  private _bitmapRotation: number = 0;
  private _imageBitmapData: HTMLImageElement = null;
  private _backGroundBitmapData: HTMLImageElement = null;
  private _selected: boolean = false;
  private _rendererAnisotropy: number;
  private _defaultDepthWrite: boolean;

  public constructor(parameters?: MeshBasicMaterialParameters, fillColor: number = 0xffffff, isTransparent: boolean = true,
    isDefaultRotation: boolean = true, rendererAnisotropy: number = SpriteMaterial.MIN_TEXTURE_ANISOTROPY_VALUE) {
    super(parameters);
    this.color = new Color('#FFFFFF');
    if (parameters) {
      this._defaultDepthWrite = parameters.depthWrite;
    }
    this.setTransparent(isTransparent);
    this._rendererAnisotropy = rendererAnisotropy;
    this._fillColor = fillColor;
    this.createRenderBitmap();
    this.needsUpdate = true;
    this._defaultRotation = isDefaultRotation ? SpriteMaterial.DEFAULT_BITMAP_ROTATION : 0;
  }

  private createRenderBitmap(): void {
    const width: number = Math.max(2, this.width);
    const height: number = Math.max(2, this.height);
    const context: CanvasRenderingContext2D = TextureUtils.createCanvasContext(width, height);
    const texture: CanvasTexture = new CanvasTexture(context.canvas);
    this._imageBitmapData = texture.image;
    this.opacity = 1;
    const oldTexture: Texture = this.map;
    if (oldTexture) {
      oldTexture.dispose();
    }
    this.map = texture;
    this.updateRenderAnisotropy();
    this.needsUpdate = true;
  }

  public setSize(width: number, height: number): void {
    this.width = (NumberUtils.isNumber(width) && width > 1) ? width : this.width;
    this.height = (NumberUtils.isNumber(height) && height > 1) ? height : this.height;

    this.createRenderBitmap();
    this.needsUpdate = true;
  }

  public set bitmapRotation(angle: number) {
    if (angle === this._bitmapRotation) {
      return;
    }
    this._bitmapRotation = angle;
    this.needsUpdate = true;
    this.updateMaterial();
  }

  public get bitmapRotation(): number {
    return this._bitmapRotation;
  }

  public setTransparent(value: boolean) {
    if (this.transparent === value) {
      return;
    }
    this.transparent = value;
    this.updateMaterial();
  }

  public updateMaterial(forceTransparency: boolean = false): void {
    if (!this.imageBitmapData) {
      return;
    }
    // for handling image height = 0 or image width = 0
    const canvasWidth: number = this.imageBitmapData.width ? this.imageBitmapData.width : this.width;
    const canvasHeight: number = this.imageBitmapData.height ? this.imageBitmapData.height : this.height;

    const context: CanvasRenderingContext2D = TextureUtils.createCanvasContext(canvasWidth, canvasHeight);
    const canvas: HTMLCanvasElement = context.canvas;

    let cw = canvas.width, ch = canvas.height, cx = 0, cy = 0;
    const angle = -this.bitmapRotation; // because positive value rotates clockwise
    // calculate new canvas size and x/y coorditates for starting draw image
    switch (angle) {
      case Math.PI / 2: // 90
        cw = canvas.height;
        ch = canvas.width;
        cy = -canvas.height;
        break;
      case Math.PI: // 180
        cx = -canvas.width;
        cy = -canvas.height;
        break;
      case -Math.PI / 2: // -90
      case 3 * Math.PI / 2: // 270
        cw = canvas.height;
        ch = canvas.width;
        cx = -canvas.width;
        break;
    }
    canvas.width = cw;
    canvas.height = ch;
    context.rotate(angle);

    this.depthWrite = this._defaultDepthWrite;
    if (!this.transparent && !forceTransparency) {
      context.fillStyle = '#' + this.fillColor.toString(16).padStart(6, '0');
      context.fillRect(cx, cy, canvasWidth, canvasHeight);
      this.depthWrite = true;
    } else if (this.backgroundBitmapData) {
      context.drawImage(this.backgroundBitmapData, cx, cy);
    }
    if (this.imageBitmapData.width > 0 && this.imageBitmapData.height > 0) {
      context.drawImage(this.imageBitmapData, cx, cy);
    }
    const texture: CanvasTexture = new CanvasTexture(canvas);
    const oldTexture: Texture = this.map;
    oldTexture.dispose();
    this.map = texture;
    this.updateRenderAnisotropy();
    this.needsUpdate = true;
  }

  private updateRenderAnisotropy(): void {
    if (this._rendererAnisotropy < SpriteMaterial.MIN_TEXTURE_ANISOTROPY_VALUE) {
      this.map.anisotropy = 1;
      this.map.minFilter = LinearFilter;
    } else {
      this.map.anisotropy = SpriteMaterial.MIN_TEXTURE_ANISOTROPY_VALUE;
    }
  }

  public get imageBitmapData(): HTMLImageElement {
    return this._imageBitmapData;
  }

  public set imageBitmapData(value: HTMLImageElement) {
    this._imageBitmapData = value;
  }

  public get backgroundBitmapData(): HTMLImageElement {
    return this._backGroundBitmapData;
  }

  public set backgroundBitmapData(value: HTMLImageElement) {
    this._backGroundBitmapData = value;
  }

  public get selected(): boolean {
    return this._selected;
  }

  public set selected(value: boolean) {
    this._selected = value;
  }

  public set fillColor(value: number) {
    if (this._fillColor === value) {
      return;
    }
    this._fillColor = value;
    this.updateMaterial();
  }

  public get fillColor(): number {
    return this._fillColor;
  }

  public clone(): this {
    const spriteMaterial: SpriteMaterial = new SpriteMaterial(TextureUtils.getBasicMaterialParametersFromMaterial(this), this._fillColor);
    spriteMaterial.width = this.width;
    spriteMaterial.height = this.height;
    spriteMaterial._fillColor = this._fillColor;
    spriteMaterial._defaultRotation = this._defaultRotation;
    spriteMaterial._bitmapRotation = this._bitmapRotation;
    spriteMaterial._imageBitmapData = this._imageBitmapData;
    spriteMaterial._rendererAnisotropy = this._rendererAnisotropy;
    return spriteMaterial as this;
  }
}
